import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, FormField, Input, Table, EmptyState, OfflineState, type Column } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { PageHeader } from "../components/PageHeader";
import { Mono } from "../components/Mono";
import { LienInviteModal } from "../components/LienInviteModal";
import { useDossierSearch } from "../lib/api";
import { useDebounced } from "../lib/useDebounced";
import { CAPS } from "../lib/caps";
import { formatDate, formatSexe } from "../lib/format";
import type { Id, Patient } from "../lib/types";

/**
 * AccueilPage — the reception home. Live patient typeahead (intra-lab search), entry points to a new
 * dossier, a new demande per patient, and the connection-link invite. Implements the 5 states:
 * offline (banner state), loading/error/empty wired through <Table>, forbidden via ProtectedRoute.
 */
export function AccueilPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const online = useUiStore((s) => s.online);

  const canCreate = useCan(CAPS.dossierCreate);
  const canDemande = useCan(CAPS.demandeCreate);
  const canInvite = useCan(CAPS.lienInvite);

  const [q, setQ] = useState("");
  const debouncedQ = useDebounced(q, 300);
  const search = useDossierSearch(debouncedQ);

  const [invite, setInvite] = useState<{ dossierId: Id; label: string } | null>(null);

  const goNewDossier = () => navigate("/reception/dossiers/nouveau");
  const goNewDemande = (p: Patient) => {
    if (!p.dossier) return;
    navigate(
      `/reception/demandes/nouvelle?dossier=${p.dossier.id}&patient=${p.id}&nom=${encodeURIComponent(
        `${p.nom} ${p.prenom}`,
      )}`,
    );
  };

  const columns: ReadonlyArray<Column<Patient>> = [
    {
      key: "patient",
      header: t("reception.list.col.patient"),
      cell: (p) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>
            {p.nom} {p.prenom}
          </span>
          <span style={{ color: token.slate, fontSize: token.fsCaption }}>{formatSexe(p.sexe)}</span>
        </div>
      ),
      sortable: true,
      sortAccessor: (p) => `${p.nom} ${p.prenom}`.toLowerCase(),
    },
    {
      key: "naissance",
      header: t("reception.list.col.naissance"),
      cell: (p) => <Mono>{formatDate(p.date_naissance)}</Mono>,
      mono: true,
    },
    {
      key: "telephone",
      header: t("reception.list.col.telephone"),
      cell: (p) => <Mono>{p.telephone}</Mono>,
      mono: true,
    },
    {
      key: "nin",
      header: t("reception.list.col.nin"),
      cell: (p) => <Mono>{p.nin}</Mono>,
      mono: true,
    },
    {
      key: "actions",
      header: t("reception.list.col.actions"),
      align: "end",
      cell: (p) => (
        <div style={{ display: "flex", gap: token.s2, justifyContent: "flex-end", flexWrap: "wrap" }}>
          {canDemande && p.dossier && (
            <Button size="sm" onClick={() => goNewDemande(p)}>
              {t("reception.action.newDemande")}
            </Button>
          )}
          {canInvite && p.dossier && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setInvite({ dossierId: p.dossier!.id, label: `${p.nom} ${p.prenom}` })}
            >
              {t("reception.action.invite")}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const tooShort = debouncedQ.trim().length < 2;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
      <PageHeader
        title={t("reception.accueil.title")}
        subtitle={t("reception.accueil.subtitle")}
        actions={
          canCreate ? (
            <Button onClick={goNewDossier} leadingIcon={<span aria-hidden>＋</span>}>
              {t("reception.action.newDossier")}
            </Button>
          ) : undefined
        }
      />

      {!online ? (
        <OfflineState onRetry={() => void search.refetch()} cached={!!search.data} />
      ) : (
        <Card>
          <FormField label={t("reception.search.label")} hint={t("reception.search.hint")}>
            {(p) => (
              <Input
                {...p}
                type="search"
                autoFocus
                placeholder={t("reception.search.placeholder")}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            )}
          </FormField>

          <div style={{ marginBlockStart: token.s4 }}>
            {tooShort ? (
              <EmptyState
                glyph="⌕"
                title={t("reception.search.label")}
                description={t("reception.search.hint")}
              />
            ) : (
              <Table<Patient>
                columns={columns}
                rows={search.data ?? []}
                rowKey={(p) => String(p.id)}
                caption={t("reception.search.label")}
                loading={search.isLoading}
                error={
                  search.isError
                    ? {
                        message: search.error?.message ?? t("reception.error.generic"),
                        onRetry: () => void search.refetch(),
                      }
                    : null
                }
                emptyTitle={t("reception.search.empty")}
                emptyDescription={t("reception.search.emptyDesc")}
                emptyAction={
                  canCreate ? (
                    <Button onClick={goNewDossier}>{t("reception.action.newDossier")}</Button>
                  ) : undefined
                }
                pageSize={10}
              />
            )}
          </div>
        </Card>
      )}

      {invite && (
        <LienInviteModal
          open
          onClose={() => setInvite(null)}
          dossierId={invite.dossierId}
          patientLabel={invite.label}
        />
      )}
    </div>
  );
}
